"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.tmdbService = exports.getPersonTopMovies = exports.getMultiplePosters = exports.getNumericRating = exports.formatPersonAge = exports.formatPersonGender = exports.getPersonProfileImage = exports.getBackdropImage = exports.getHighQualityPoster = void 0;
// Import constants from central location
const constants_1 = require("../constants");
// =============== UTILITY FUNCTIONS ===============
/**
 * Gets a high-quality poster URL from a poster path
 */
const getHighQualityPoster = (posterPath) => {
    if (!posterPath)
        return '';
    const baseUrl = posterPath.startsWith('http') ? '' : constants_1.TMDB_IMAGE_BASE_URL;
    return `${baseUrl}${posterPath}`;
};
exports.getHighQualityPoster = getHighQualityPoster;
/**
 * Gets a backdrop image URL
 */
const getBackdropImage = (backdropPath) => {
    if (!backdropPath)
        return '';
    const baseUrl = backdropPath.startsWith('http') ? '' : 'https://image.tmdb.org/t/p/w1280';
    return `${baseUrl}${backdropPath}`;
};
exports.getBackdropImage = getBackdropImage;
/**
 * Gets a person profile image URL
 */
const getPersonProfileImage = (profilePath, size = 'h632') => {
    if (!profilePath)
        return '';
    return `https://image.tmdb.org/t/p/${size}${profilePath}`;
};
exports.getPersonProfileImage = getPersonProfileImage;
/**
 * Formats person gender
 */
const formatPersonGender = (gender) => {
    switch (gender) {
        case 1: return 'Female';
        case 2: return 'Male';
        case 3: return 'Non-binary';
        default: return 'Not specified';
    }
};
exports.formatPersonGender = formatPersonGender;
/**
 * Formats person age from birthday and deathday
 */
const formatPersonAge = (birthday, deathday = null) => {
    if (!birthday)
        return '';
    const birthDate = new Date(birthday);
    const endDate = deathday ? new Date(deathday) : new Date();
    const age = endDate.getFullYear() - birthDate.getFullYear();
    return deathday ? `${age} (deceased)` : `${age} years old`;
};
exports.formatPersonAge = formatPersonAge;
/**
 * Gets numeric rating from ratings array and TMDB rating
 */
const getNumericRating = (ratings, tmdbRating) => {
    // Try to get IMDb rating first
    if (ratings) {
        const imdbRating = ratings.find(r => r.Source === 'Internet Movie Database');
        if (imdbRating) {
            return parseFloat(imdbRating.Value.split('/')[0]);
        }
    }
    // Fall back to TMDB rating (convert from 10-point to 5-point scale)
    if (tmdbRating) {
        return Math.round((parseFloat(tmdbRating) / 2) * 10) / 10;
    }
    return 0;
};
exports.getNumericRating = getNumericRating;
/**
 * Gets multiple poster URLs for a movie
 */
const getMultiplePosters = (movie) => {
    var _a;
    const posters = [];
    // Add main poster
    if (movie.poster_path) {
        posters.push((0, exports.getHighQualityPoster)(movie.poster_path));
    }
    // Add additional posters from images
    if ((_a = movie.images) === null || _a === void 0 ? void 0 : _a.posters) {
        movie.images.posters
            .filter(poster => poster.file_path !== movie.poster_path)
            .slice(0, 4) // Limit to 4 additional posters
            .forEach(poster => {
            posters.push((0, exports.getHighQualityPoster)(poster.file_path));
        });
    }
    return posters;
};
exports.getMultiplePosters = getMultiplePosters;
/**
 * Gets top movies for a person
 */
const getPersonTopMovies = (person, limit = 10) => {
    var _a, _b;
    const movies = [];
    // Add cast movies
    if ((_a = person.movie_credits) === null || _a === void 0 ? void 0 : _a.cast) {
        person.movie_credits.cast.forEach(movie => {
            movies.push({
                id: movie.id,
                title: movie.title,
                character: movie.character,
                poster_path: movie.poster_path,
                release_date: movie.release_date,
                vote_average: movie.vote_average
            });
        });
    }
    // Add crew movies
    if ((_b = person.movie_credits) === null || _b === void 0 ? void 0 : _b.crew) {
        person.movie_credits.crew.forEach(movie => {
            movies.push({
                id: movie.id,
                title: movie.title,
                job: movie.job,
                poster_path: movie.poster_path,
                release_date: movie.release_date,
                vote_average: movie.vote_average
            });
        });
    }
    // Sort by vote average and return top movies
    return movies
        .sort((a, b) => b.vote_average - a.vote_average)
        .slice(0, limit);
};
exports.getPersonTopMovies = getPersonTopMovies;
// =============== SERVICE INSTANCE ===============
// For shared package usage, we export the utility functions as a service object
exports.tmdbService = {
    getHighQualityPoster: exports.getHighQualityPoster,
    getBackdropImage: exports.getBackdropImage,
    getPersonProfileImage: exports.getPersonProfileImage,
    formatPersonGender: exports.formatPersonGender,
    formatPersonAge: exports.formatPersonAge,
    getNumericRating: exports.getNumericRating,
    getMultiplePosters: exports.getMultiplePosters,
    getPersonTopMovies: exports.getPersonTopMovies,
};
